import { useMemo, useState } from 'react'
import { Tabs } from 'antd'
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons/faClipboardList'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { RankmiTable } from '@rankmi/rankmi-table'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import EmptyState from 'components/reusables/emptyState'
import { surveyTemplatesService } from 'api/administration/surveyTemplates'
import { useNotification } from 'hooks/notifications/useNotification'
import type {
  SurveyTemplateRow,
  SurveyTemplateStatus,
} from '../../common/types'
import { TRANSLATION_PREFIX } from '../../constants'
import { useSurveyTemplates } from '../../hooks/useSurveyTemplates'
import { ArchiveConfirmation } from '../ArchiveConfirmation'
import { SurveyTemplateOptions } from '../SurveyTemplateOptions'
import {
  DATE_COLUMN_WIDTH,
  Description,
  ListContainer,
  StatusTabs,
  Name,
  NameCell,
  NAME_COLUMN_WIDTH,
  NUMBER_COLUMN_WIDTH,
  OPTIONS_COLUMN_WIDTH,
  TableContainer,
  Title,
} from './styled'

const DATE_FORMAT = 'dd/MM/yyyy'
const { TabPane } = Tabs

interface IProps {
  onCreate: () => void
  onEdit: (row: SurveyTemplateRow) => void
}

/** Survey bank listing: search, active/archived filter and row actions. */
export const SurveyTemplateList = ({ onCreate, onEdit }: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const {
    rows,
    total,
    loading,
    failed,
    filters,
    setSearch,
    setStatus,
    setPage,
    setPerPage,
    reload,
  } = useSurveyTemplates()
  const [busyId, setBusyId] = useState<number | null>(null)
  const [toArchive, setToArchive] = useState<SurveyTemplateRow | null>(null)
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`${TRANSLATION_PREFIX}.${key}`, options)

  const runAction = async (
    row: SurveyTemplateRow,
    action: (id: number) => Promise<unknown>,
    successKey: string
  ) => {
    setBusyId(row.id)
    try {
      await action(row.id)
      notification.success(text(successKey, { name: row.name }))
      reload()
    } catch {
      notification.error(text('errors.action'))
    } finally {
      setBusyId(null)
    }
  }

  const confirmArchive = async () => {
    if (!toArchive) return
    await runAction(
      toArchive,
      surveyTemplatesService.archive,
      'archive.success'
    )
    setToArchive(null)
  }

  const columns = useMemo(
    () => [
      {
        title: t(`${TRANSLATION_PREFIX}.table.headers.name`),
        key: 'name',
        sorteable: false,
        hideable: false,
        fixed: 'left',
        width: NAME_COLUMN_WIDTH,
        render: (name: string, row: SurveyTemplateRow) => (
          <NameCell>
            <Name data-testid="survey-bank-row-name" title={name}>
              {name}
            </Name>
            {row.description && <Description>{row.description}</Description>}
          </NameCell>
        ),
      },
      {
        title: t(`${TRANSLATION_PREFIX}.table.headers.questions`),
        key: 'questionsCount',
        sorteable: false,
        width: NUMBER_COLUMN_WIDTH,
        render: (count: number) => (
          <span data-testid="survey-bank-row-questions">{count}</span>
        ),
      },
      {
        title: t(`${TRANSLATION_PREFIX}.table.headers.updatedAt`),
        key: 'updatedAt',
        sorteable: false,
        width: DATE_COLUMN_WIDTH,
        render: (updatedAt: string) => format(parseISO(updatedAt), DATE_FORMAT),
      },
      {
        title: '',
        key: 'id',
        sorteable: false,
        hideable: false,
        fixed: 'right',
        width: OPTIONS_COLUMN_WIDTH,
        align: 'center',
        render: (_id: number, row: SurveyTemplateRow) => (
          <SurveyTemplateOptions
            row={row}
            busy={busyId === row.id}
            onEdit={onEdit}
            onDuplicate={(target) =>
              runAction(
                target,
                surveyTemplatesService.duplicate,
                'duplicate.success'
              )
            }
            onArchive={setToArchive}
            onRestore={(target) =>
              runAction(
                target,
                surveyTemplatesService.restore,
                'restore.success'
              )
            }
          />
        ),
      },
    ],
    // runAction and onEdit read the latest state; the columns only depend on
    // the texts and on which row is busy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, busyId]
  )

  const locale = useMemo(
    () => ({
      header: {
        rowsCount: t(`${TRANSLATION_PREFIX}.table.rowsCount`),
        rowsCount_plural: t(`${TRANSLATION_PREFIX}.table.rowsCount_plural`),
        search: t(`${TRANSLATION_PREFIX}.table.searchPlaceholder`),
        addButton: t(`${TRANSLATION_PREFIX}.createTemplate`),
      },
      // Reuses the rows-per-page label of the course list, already translated.
      controls: {
        rowsPerPage: t(
          'administration.courses.list.table.controls.rowsPerPage'
        ),
      },
      table: { emptyMessage: t(`${TRANSLATION_PREFIX}.table.noResults`) },
    }),
    [t]
  )

  // Archived surveys are only restored: new surveys are created from the
  // active list or from a course survey block.
  const canCreate = filters.status === 'active'

  // After a failed load the bank is unknown, not empty: keep the table.
  const showEmptyBank =
    !loading && !failed && rows.length === 0 && !filters.search && canCreate

  const listBody = showEmptyBank ? (
    <div data-testid="survey-bank-empty">
      <EmptyState
        icon={faClipboardList}
        title={text('empty.heading')}
        description={text('empty.hint')}
        action={onCreate}
        actionIcon={faPlus}
        actionTitle={text('createTemplate')}
      />
    </div>
  ) : (
    <TableContainer data-testid="survey-bank-table">
      <RankmiTable
        columnHeaders={columns}
        data={rows}
        countIcon={faClipboardList}
        addButtonIcon={faPlus}
        onAddClick={canCreate ? onCreate : undefined}
        search={filters.search}
        onSearch={setSearch}
        total={total}
        locale={locale}
        loading={loading}
        currentPage={filters.page}
        onPaginationChange={setPage}
        initialRowsPerPage={filters.perPage}
        onRowsPerPageChange={setPerPage}
        scroll={{ x: 'max-content' }}
        delaySearch
        responsive
      />
    </TableContainer>
  )

  return (
    <ListContainer data-testid="survey-bank-list">
      <Title>{text('pageTitle')}</Title>
      <StatusTabs data-testid="survey-bank-status-filter">
        <Tabs
          activeKey={filters.status}
          onChange={(status) => setStatus(status as SurveyTemplateStatus)}
        >
          <TabPane
            key="active"
            tab={
              <span data-testid="survey-bank-status-active">
                {text('status.active')}
              </span>
            }
          />
          <TabPane
            key="archived"
            tab={
              <span data-testid="survey-bank-status-archived">
                {text('status.archived')}
              </span>
            }
          />
        </Tabs>
      </StatusTabs>
      {listBody}
      {toArchive && (
        <ArchiveConfirmation
          row={toArchive}
          processing={busyId === toArchive.id}
          onConfirm={confirmArchive}
          onClose={() => setToArchive(null)}
        />
      )}
    </ListContainer>
  )
}
